import React, { Ref } from 'react';
import { cn } from '@/lib';

/**
 * Slot component for asChild pattern (inspired by Radix UI)
 * Merges props with the child element instead of wrapping it in an extra DOM node.
 *
 * This is useful when you want to pass a custom component that should receive
 * the parent's props (like onClick, className, etc.) without adding wrapper elements.
 *
 * @example
 * // Accordion with custom trigger (StanDataRow instead of default button):
 * <StanAccordionHeader asChild>
 *   <StanDataRow columns={[...]} onClick={customHandler} />
 * </StanAccordionHeader>
 *
 * // The StanDataRow will receive the accordion's onClick handler merged with its own,
 * // allowing it to toggle the accordion while maintaining its own functionality.
 *
 * @example
 * // Common pattern in component libraries:
 * function Trigger({ asChild, children, ...props }) {
 *   const Comp = asChild ? Slot : 'button';
 *   return <Comp {...props}>{children}</Comp>;
 * }
 */
interface SlotProps extends React.HTMLAttributes<HTMLElement> {
    children?: React.ReactNode;
}

export const Slot = ({ children, ref, ...props }: SlotProps & { ref?: Ref<HTMLElement> }) => {
    if (React.isValidElement(children)) {
        const childProps = children.props as any;

        // Extract onClick from both parent and child to merge them
        const { onClick: parentOnClick, ...restParentProps } = props;
        const { onClick: childOnClick, ...restChildProps } = childProps;

        return React.cloneElement(children, {
            ...restParentProps,
            ...restChildProps,
            ref,

            // Merge onClick handlers
            onClick: (e: React.MouseEvent<HTMLElement>) => {
                try {
                    // Call parent handler first
                    parentOnClick?.(e);

                    // Only call child handler if event wasn't stopped
                    if (!e.isPropagationStopped() && !e.defaultPrevented) {
                        childOnClick?.(e);
                    }
                } catch (error) {
                    console.error('Error in Slot onClick handler:', error);
                    throw error;
                }
            },
            // Merge className
            className: cn(props.className, childProps?.className),
            // Merge style
            style: {
                ...childProps?.style,
                ...props.style,
            },
        } as any);
    }

    return <>{children}</>;
};

Slot.displayName = 'Slot';
